import React, { FC, useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Paper,
  Divider,
} from '@material-ui/core';
import Icon from '@mdi/react';
import Pagination from '@material-ui/lab/Pagination';

//icons
import {
  mdiAccountKeyOutline,
  mdiAccountOutline,
  mdiChatOutline,
  mdiClose,
  mdiHelp,
  mdiPlus,
  mdiPlusBoxOutline,
} from '@mdi/js';

//components
import List from './components/List';
import theme from '../../theme';
import Modal from '../../components/Modal';
import Add from './components/Add';

//helpers
import RenderFilter, { Filter } from '../../utils/listDaynamicFilterHookForm';
import { removeEmptyValueObject, checkMatchMedia } from '../../utils/helpers';
import useQuery from '../../utils/hook/useQuery';
import windowSize from '../../utils/hook/useWindowResize';

// redux
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Store } from '../../redux/reducers';
import {
  makeSelectProvidersList,
  makeSelectCountList,
  makeSelectError,
  makeSelectLoading,
  makeSelectActiveCardCountList,
  makeSelectInactiveCardCountList,
} from './selectors';

// types and interfaces
import { addProvider, Payload, PayloadCreate, Props, State } from './interfaces';
import {
  createProviderAction,
  getProvidersAction,
  updateProviderStatus,
} from './actions';

// context api
import AuthContext from '../../authContext';
import Can from '../../components/Can';
import { Active, Inactive } from './customIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    textArea: {
      width: '100%',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      padding: '10px 0',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },
    cards: {
      position: 'relative',
      backgroundColor: 'white',
      padding: '14px',
    },
    boxshadow: {
      position: 'relative',
      backgroundColor: 'white',
      padding: '14px',
      transform: 'translateY(-3px)',
      boxShadow: '-4px 4px 12px 0px rgba(0,0,0,0.15) !important',
    },
    iconHeader: {
      position: 'absolute',
      top: '5px',
      right: '5px',
    },
    cardContent: {
      marginTop: '-22px',
      display: 'flex',
    },
    hover: {
      '&:hover': {
        curser: 'pointer',
      },
    },
    chart: {
      padding: '24px',
      height: '312px',
      backgroundColor: 'white',
      width: '100%',
      overflow: 'visible',
    },
    progress: {
      padding: '24px',
      height: '312px',
      backgroundColor: 'white',
      width: '100%',
      overflow: 'visible',
    },
    typographyCards: {
      fontSize: '16px',
      color: 'white',
      backgroundColor: '#4BCF90',
      padding: '8px 24px 8px 50px',
      marginLeft: '-40px',
    },
    card: {
      width: '100%',
      // marginBottom: '14px',
      overflow: 'visible',
      padding: '14px 0 0',
      paddingBottom: '0',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      marginRight: '10px',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '30px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },

    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      marginTop: '45px',

      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(1),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    btnBox: {
      marginTop: '5px',
      padding: '10px',
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
      '@media (max-width: 768px)': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },
    headerBox: {
      marginLeft: '75px',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
      marginTop: '10px',
    },
    cardContentOrder: {
      padding: '0',
    },
    cardContentlist: {
      justifyContent: 'space-between',
      padding: ' 0',
    },
  })
);

const Providers: FC<Props> = (props: Props): React.ReactElement => {
  const authContext = useContext(AuthContext);
  const [openSize, setOpenSize] = useState(false as boolean);
  const [openConfirmDisable, setOpenConfirmDisable] = useState(
    false as boolean
  );
  const [openConfirmActive, setOpenConfirmActive] = useState(false as boolean);
  const [openAddModal, setOpenAddModal] = useState(false as boolean);
  const [id, setId] = useState(-1 as number);
  const [filters, setFilters] = useState([] as Filter[]);

  const { filtersValues, setFiltersValues } = useQuery({
    partnerId: { equals: localStorage.getItem('partnerId') },
    page: 0,

    size: 10,
  });

  const [formData, setFormData] = useState(null as null | addProvider);
  const [checkAddProvider, setCheckAddProvider] = useState(false as boolean);

  const classes = useStyles();
  const [isMob, setIsMob] = useState(false);
  const { width } = windowSize();

  useEffect(() => {
    getProviders();
    setupFilters();
  }, [filtersValues]);

  useEffect(() => {
    if (width && width <= 480) {
      setIsMob(true);
    } else {
      setIsMob(false);
    }
  }, [width]);

  const getProviders = () => {
    const { page, size } = filtersValues;
    props.providersDispatch({
      ...removeEmptyValueObject(filtersValues),
      page,
      size,
    });
  };

  const handleClose = () => {
    setOpenConfirmDisable(false);
    setOpenConfirmActive(false);
    setOpenSize(false);
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({
      ...filtersValues,
      page: 0,
      size: Number(event.target.value),
    });
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };

  const handleOpen = (id: number) => {
    setId(id);
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'name.contains',
        label: 'نام تامین کننده',
        icon: mdiAccountOutline,
      },
      {
        type: 'number',
        id: 'id.equals',
        label: 'شناسه تامین کننده',
        icon: mdiAccountKeyOutline,
      },
      {
        type: 'input',
        id: 'code.contains',
        label: 'کد تامین کننده',
        icon: mdiAccountOutline,
      },
    ]);
  };

  const handleOpenConfirmDisable = (id: number) => {
    setOpenConfirmDisable(true);
    setId(id);
  };
  const handleOpenConfirmActive = (id: number) => {
    setOpenConfirmActive(true);
    setId(id);
  };

  const handleDisableProvider = async () => {
    await props.updateProviderStatusDispatch({
      id,
      body: 'INACTIVE'.replace(/'/g, '"'),
      pagination: filtersValues,
    });
    setOpenConfirmDisable(false);
  };

  const handleActiveProvider = async () => {
    await props.updateProviderStatusDispatch({
      id,
      body: 'ACTIVE'.replace(/'/g, '"'),
      pagination: filtersValues,
    });

    setOpenConfirmActive(false);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: any }) => {
    setId(data.id);
  };

  const handleCreateProvider = (body: addProvider) => {
    setFormData(body);
    setCheckAddProvider(true);
  };

  const handleCloseCheck = () => {
    setCheckAddProvider(false);
    setFormData(null);
  };

  const handleSubmitAddProvider = () => {
    const callback = () => {
      setCheckAddProvider(false);
      setOpenAddModal(false);
      setFormData(null);
    };
    const { name, code }: addProvider = formData!;
    props.createProviderDispatch({
      body: {
        name: name,
        code: code,
      },
      pagination: filtersValues,
      callback,
    });
  };

  const handleCardClose = () => {
    const { page, size } = filtersValues;
    delete filtersValues['status.in'];
    setFiltersValues({
      ...removeEmptyValueObject(filtersValues),

      page: 0,
      size: 10,
    });
  };

  const handleCardClick = (name: any) => {
    const { page, size } = filtersValues;

    switch (name) {
      case 'activeProvider':
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          'status.in': ['ACTIVE'],
          page: 0,
          size: 10,
        });
        break;
      case 'inactiveProvider':
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          'status.in': ['INACTIVE'],
          page: 0,
          size: 10,
        });
        break;
    }
  };

  const checkActiveCard = (name: string) => {
    const status = filtersValues['status.in']?.split(',') || [];
    switch (name) {
      case 'activeProvider':
        return status.includes('ACTIVE');
      case 'inactiveProvider':
        return status.includes('INACTIVE');
    }
  };

  return (
    <div className={classes.root}>
      {/* <Grid
        style={{
          zIndex: 3,
          backgroundColor: 'white',
          position: 'fixed',
          top: '0',
          right: '71px',
        }}
        container
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <Paper
          elevation={1}
          style={{
            padding: '5px 2px',
            width: '100%',
            borderRadius: '0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div className={classes.headerBox}>
            
          </div>
          <div
            style={{
              marginLeft: '140px',
              display: 'flex'
            }}
          >
            <RenderFilter
              filters={filters}
              onFilter={(filtervalues: any) => {
                setFiltersValues({
                  ...removeEmptyValueObject(filtervalues),
                  partnerId: { equals: localStorage.getItem('partnerId') },
                  page: 0,
                  size: 10,
                });
              }}
              mainFiltersCount={1}
              baseFilter={{
                type: 'input',
                id: 'name.contains',
                placeholder: 'نام تامین کننده',
              }}
              filterValues={filtersValues}
            />
            <TicketLink />
          </div>
        </Paper>
      </Grid> */}
      <Typography variant="h6" component="h2">
        {'مدیریت تامین کنندگان'}
      </Typography>
      <Grid
        container
        spacing={2}
        md={12}
        style={{
          // justifyContent: 'space-between',
          maxWidth: '120%',
          margin: '24px 0 24px 0',
        }}
      >
        <Grid
          container
          lg={12}
          spacing={2}
          xs={12}
          style={{ marginRight: '-7px' }}
        >
          <Grid item md={6} lg={6} xs={12}>
            <Card
              className={
                checkActiveCard('activeProvider')
                  ? classes.boxshadow
                  : classes.cards
              }
            >
              <CardHeader
                action={
                  checkActiveCard('activeProvider') && (
                    <IconButton
                      aria-label="close"
                      onClick={handleCardClose}
                      className={classes.iconHeader}
                    >
                      <Icon
                        path={mdiClose}
                        size={1}
                        style={{
                          color: '#Ffffff',
                          backgroundColor: '#FFA65A',
                          borderRadius: '3px',
                        }}
                      />
                    </IconButton>
                  )
                }
              />
              <CardContent className={classes.cardContent}>
                <IconButton
                  type="submit"
                  onClick={name => handleCardClick('activeProvider')}
                  aria-label="search"
                  style={{
                    backgroundColor: '#D6EBD3',
                    borderRadius: '4px',
                    margin: '-20px 0',
                  }}
                >
                  {Active()}
                </IconButton>
                <Grid
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{ fontWeight: 600, marginTop: '-20px' }}
                  >
                    {props.activeCardCount}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '16px',
                      marginBottom: '-20px',
                      fontWeight: 550,
                      color: '#666666',
                    }}
                  >
                    تامین‌کننده فعال
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item md={6} lg={6} xs={12}>
            <Card
              className={
                checkActiveCard('inactiveProvider')
                  ? classes.boxshadow
                  : classes.cards
              }
            >
              <CardHeader
                action={
                  checkActiveCard('inactiveProvider') && (
                    <IconButton
                      aria-label="close"
                      onClick={handleCardClose}
                      className={classes.iconHeader}
                    >
                      <Icon
                        path={mdiClose}
                        size={1}
                        style={{
                          color: '#Ffffff',
                          backgroundColor: '#FFA65A',
                          borderRadius: '3px',
                        }}
                      />
                    </IconButton>
                  )
                }
              />
              <CardContent className={classes.cardContent}>
                <IconButton
                  type="submit"
                  aria-label="search"
                  onClick={name => handleCardClick('inactiveProvider')}
                  style={{
                    backgroundColor: '#FCD4DA',
                    borderRadius: '4px',
                    margin: '-20px 0',
                  }}
                >
                  {Inactive()}
                </IconButton>
                <Grid
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{ fontWeight: 600, marginTop: '-20px' }}
                  >
                    {props.inactiveCardCount}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '16px',
                      marginBottom: '-20px',
                      fontWeight: 550,
                      color: '#666666',
                    }}
                  >
                    تامین‌کننده غیرفعال
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Card className={classes.card}>
          <CardHeader
            avatar={
              <Grid>
                <Typography className={classes.typographyCards}>
                  لیست تامین‌کنندگان
                </Typography>
              </Grid>
            }
            action={
              <div className={classes.btnBox}>
                <Can
                  role={authContext.user.role}
                  perform="providers:add"
                  yes={() => (
                    <Button
                      variant="contained"
                      color="primary"
                      size={
                        checkMatchMedia('(max-width: 1024px)')
                          ? 'small'
                          : 'large'
                      }
                      startIcon={<Icon path={mdiPlusBoxOutline} size={1} />}
                      onClick={() => {
                        props.history.push('/providerAdd');
                      }}
                    >
                      {'ایجاد تامین کننده '}
                    </Button>
                  )}
                />
              </div>
            }
          />

          <CardContent className={classes.cardContentOrder}>
            <Grid container lg={12} md={12} className={classes.cardContentlist}>
              <Grid container item lg={12} md={12}>
                <Grid
                  container
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  alignItems="center"
                >
                  {props.loading ? (
                    <div className={classes.loading}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <>
                      {props.providerList.length > 0 ? (
                        <List
                          data={props.providerList}
                          changeStatus={handleOpenConfirmUpdateState}
                          handleOpenConfirmDisable={handleOpenConfirmDisable}
                          handleOpenConfirmActive={handleOpenConfirmActive}
                          handleOpen={handleOpen}
                        />
                      ) : (
                        <Box mt={4}>
                          <Typography variant="h3" component="div">
                            تامین کننده‌ای موجود نیست
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} style={{ padding: '20px 10px 0 10px' }}>
                  {props.providerList.length > 0 && (
                    <Grid container>
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ marginLeft: '10px', color: '#8a8a8a' }}>
                            {' '}
                            تعداد نمایش در هر صفحه:
                          </div>
                          <Select
                            open={openSize}
                            onClose={handleClose}
                            onOpen={() => setOpenSize(true)}
                            value={filtersValues.size}
                            onChange={handleChangeSizePage}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                {props.providerList.length > 0 && (
                  <Grid
                    xs={12}
                    sm={6}
                    container
                    item
                    direction="row-reverse"
                    style={{ padding: '20px 10px 0 10px' }}
                  >
                    <Pagination
                      count={Math.ceil(props.count / filtersValues.size)}
                      color="secondary"
                      page={filtersValues.page + 1}
                      onChange={handleChangePage}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Dialog
        open={openConfirmDisable || openConfirmActive}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '3px',
              top: '10px',
              zIndex: 10,
            }}
          >
            {handleClose ? (
              <IconButton aria-label="close" onClick={handleClose}>
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div className={classes.cancelTitle}>
            {` آیا از 
          
          ${openConfirmDisable ? 'غیر' : ''}
          
          فعال کردن تامین کننده با نام
          "${props.providerList.find(provider => provider.id === id)?.name}"
          اطمینان دارید؟`}
          </div>
          <Divider style={{ width: '100%', margin: '16px 0  0' }} />
        </DialogTitle>
        <DialogActions>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            style={{ textAlign: 'center', marginBottom: '8px' }}
          >
            <Grid item xs={12}>
              <Button
                style={{ marginLeft: '8px' }}
                autoFocus
                onClick={handleClose}
                variant="outlined"
                color="primary"
                size="large"
              >
                {`انصراف`}
              </Button>
              {openConfirmDisable ? (
                <Button
                  onClick={handleDisableProvider}
                  variant="contained"
                  size="large"
                  color="primary"
                >
                  {`غیرفعال کردن`}
                </Button>
              ) : (
                <Button
                  onClick={handleActiveProvider}
                  variant="contained"
                  size="large"
                  color="primary"
                >
                  {`فعال کردن`}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <Modal
        onClose={() => setOpenAddModal(false)}
        open={openAddModal}
        title={
          <div className={classes.addIcon}>
            <Icon path={mdiPlus} size={4} color={theme.palette.green.main} />
          </div>
        }
        content={
          <Add
            onClose={() => setOpenAddModal(false)}
            count={props.count + 1}
            error={props.error}
            createProvider={handleCreateProvider}
          />
        }
      />

      <Dialog
        fullWidth
        open={checkAddProvider}
        onClose={handleCloseCheck}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            {handleCloseCheck ? (
              <IconButton aria-label="close" onClick={handleCloseCheck}>
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div className={classes.editIcon}>
            <Icon path={mdiHelp} size={4} color={theme.palette.blue.main} />
          </div>
          <div className={classes.cancelTitle}>
            {`آیا از ایجاد کردن تامین کننده ${formData?.name} اطیمنان دارید؟`}
          </div>
        </DialogTitle>
        <DialogActions>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={handleCloseCheck}
                variant="contained"
                size="large"
              >
                {`خیر`}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                onClick={handleSubmitAddProvider}
                fullWidth
                variant="contained"
                size="large"
                color="secondary"
                disabled={props.loading}
              >
                {props.loading ? (
                  <CircularProgress size={24} color="secondary" />
                ) : (
                  'بله'
                )}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = () =>
  createStructuredSelector<Store, State>({
    providerList: makeSelectProvidersList(),
    count: makeSelectCountList(),
    loading: makeSelectLoading(),
    error: makeSelectError(),
    inactiveCardCount: makeSelectInactiveCardCountList(),
    activeCardCount: makeSelectActiveCardCountList(),
  });

function mapDispatchToProps(dispatch: Function) {
  return {
    providersDispatch: (payload: Payload) =>
      dispatch(getProvidersAction(payload)),
    updateProviderStatusDispatch: (payload: {
      id: number;
      body: string;
      pagination: object;
    }) => dispatch(updateProviderStatus(payload)),
    createProviderDispatch: (payload: PayloadCreate) =>
      dispatch(createProviderAction(payload)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Providers);
